import { List, Avatar, Badge, Typography, Spin, Drawer } from 'antd';
import { Notification } from '@/types';
import './index.scss';
import { FC, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AvatarPlaceholder } from '../shared/avatar-placeholder';
import { UserOutlined } from '@ant-design/icons';

const { Text, Link } = Typography;

interface NotificationsComponentProps {
  notifications?: Notification[];
  visible: boolean;
  onMarkAsRead: () => void;
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onClose: () => void;
}

const NotificationsComponent: FC<NotificationsComponentProps> = ({
  notifications = [],
  visible,
  onMarkAsRead,
  loading = false,
  hasMore = false,
  onLoadMore,
  onClose,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const isMobile = window.innerWidth < 768; // Simple mobile detection

  useEffect(() => {
    const handleScroll = (e: Event) => {
      if (!listRef.current || loading || !hasMore) return;

      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop <= clientHeight + 50) {
        onLoadMore?.();
      }
    };

    const currentRef = listRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, hasMore, onLoadMore]);

  const renderNotificationsList = () => (
    <>
      <div className="notifications-header">
        <Text className="notifications-title">Notifications</Text>
        <Link className="mark-as-read" onClick={onMarkAsRead}>
          Mark as Read
        </Link>
      </div>
      <div
        ref={listRef}
        className="notifications-list"
        style={{
          maxHeight: isMobile ? 'calc(80vh - 60px)' : '400px',
          overflowY: 'auto',
        }}
      >
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item className="notification-item">
              <List.Item.Meta
                avatar={
                  <div className="avatar-container">
                    {item.avatar ? (
                      <Avatar
                        src={item.avatar}
                        size={48}
                        icon={<UserOutlined />}
                      />
                    ) : (
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          overflow: 'hidden',
                          borderRadius: '50%',
                          fontSize: 18,
                        }}
                      >
                        <AvatarPlaceholder
                          firstName={
                            item?.firstName ? item.firstName : 'Anonymous'
                          }
                          lastName={
                            item?.lastName ? item.lastName : 'Anonymous'
                          }
                        />
                      </div>
                    )}
                  </div>
                }
                description={
                  <>
                    {item.message && (
                      <div className="notification-details">
                        {!item.isRead && (
                          <Badge className="unread-badge" color="#2A4BC2" dot />
                        )}
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {item.message}
                        </Text>
                      </div>
                    )}
                    <Text type="secondary" className="timestamp">
                      {formatDistanceToNow(new Date(item.createdAt), {
                        addSuffix: true,
                      })}
                    </Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
        {loading && (
          <div style={{ textAlign: 'center', padding: '12px' }}>
            <Spin />
          </div>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        open={visible}
        onClose={onClose}
        height="80vh"
        closable={false}
        title={
          <div className="drawer-handle-container">
            <div className="drawer-handle" />
          </div>
        }
        styles={{
          header: {
            borderBottom: 'none',
            padding: '8px 16px',
            textAlign: 'center',
          },
          body: {
            padding: '0 16px 16px',
            overflowY: 'hidden',
          },
        }}
      >
        {renderNotificationsList()}
      </Drawer>
    );
  }
  if (!visible) return null;

  return (
    <>
      <div className="notifications-overlay" onClick={onClose} />
      <div className="notifications-container">{renderNotificationsList()}</div>
    </>
  );
};

export { NotificationsComponent };
